import { tz } from '@date-fns/tz'
import { normalizeDate } from '@utils/helpers'
import { formatIntl } from '@utils/utils-format'
import { isSameDay, isValid } from 'date-fns'
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone'

type UseDateLabelParams = {
  fromDate: string | Date | null | undefined
  toDate: string | Date | null | undefined
  formatFromOptions?: Intl.DateTimeFormatOptions
  formatToOptions?: Intl.DateTimeFormatOptions
  fromTimeZone?: string | null
  toTimeZone?: string | null
  enablePlaceholder?: boolean
  placeholder?: string
}

export function useDateLabel({
  fromDate,
  toDate,
  formatFromOptions = { day: '2-digit', month: '2-digit' },
  formatToOptions = { day: '2-digit', month: '2-digit' },
  fromTimeZone,
  toTimeZone,
  enablePlaceholder = true,
  placeholder,
}: UseDateLabelParams) {
  if (enablePlaceholder && !fromDate && !toDate) {
    return placeholder || 'Zeitraum wählen'
  }

  const fromZone = fromTimeZone ?? DEFAULT_TIME_ZONE
  const toZone = toTimeZone ?? fromZone
  const from = normalizeDate(fromDate, fromZone)
  const to = normalizeDate(toDate, toZone)

  if (!from || !isValid(from)) return 'ab heute'

  const isToday = isSameDay(new Date(), from, { in: tz(DEFAULT_TIME_ZONE) })

  if (!fromDate || (isToday && !toDate)) {
    return 'ab heute'
  }

  const formatFrom = formatIntl({ timeZone: fromZone, ...formatFromOptions })
  const formatTo = formatIntl({ timeZone: toZone, ...formatToOptions })

  if (!to || !isValid(to) || isSameDay(from, to, { in: tz(DEFAULT_TIME_ZONE) }))
    return formatFrom(from)

  return `${formatFrom(from)} – ${formatTo(to)}`
}

export const formatDateWithYearShort: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
}
